"use client";
import { cn } from "@/lib/utils";
import { IBM_Plex_Mono } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";
import {  useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useTranslations } from "next-intl";

// // Register ScrollTrigger plugin
// if (typeof window !== "undefined") {
//   gsap.registerPlugin(ScrollTrigger);
// }

const ibmPlexMono = IBM_Plex_Mono({
  variable: "--font-open-sans",
  subsets: ["latin"],
  display: "swap",
  weight: ["400", "500", "600", "700"],
});

const BrandStorySection = () => {
  const [active, setActive] = useState<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const pinRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslations('about-us');
  
  const dataBrandStory = {
    title: t('the-brand-story-of-hyra-network'),
  };

  const dataFeatures = [
    {
      feature: t('vision'),
      image: "/images/about-us/story_4.png",
      background: "/images/backgrounds/background_features-5.png",
      description: t('hyra-network-brings-innovation-collaboration-and-sustainable-growth-creating-a-strong-and-inclusive-ecosystem-where-everyone-has-the-opportunity-to-access-and-benefit-from-ai-technology'),
    },
    {
      feature: t('edge-opportunity'),
      image: "/images/about-us/story.png",
      background: "/images/backgrounds/background_features-6.png",
      description: t('amid-the-ai-boom-hyra-network-turns-hardware-limits-and-high-costs-into-opportunity-tapping-the-unused-power-of-billions-of-edge-devices'),
    },
    {
      feature: t('solution-and-value'),
      image: "/images/about-us/story_2.png",
      background: "/images/backgrounds/background_features-7.png",
      description: t('by-converting-every-edge-device-into-decentralized-ai-hyra-helps-smbs-get-past-hurdles-while-giving-manufacturers-a-compelling-value-story'),
    },
    {
      feature: t('community-promise'),
      image: "/images/about-us/story_3.png",
      background: "/images/backgrounds/background_features-8.png",
      description: t('beyond-tech-hyra-lets-users-contribute-get-rewarded-fairly-and-drive-an-inclusive-ai-future'),
    },
  ];

  useGSAP(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const scrollDistance = scrollContainer.scrollHeight - scrollContainer.clientHeight;

    ScrollTrigger.create({
      trigger: pinRef.current,
      pin: true,
      start: "top top",
      end: "+=100%", 
      scrub: 1,
      
      onUpdate: (self) => {
        gsap.to(scrollContainer, {
          scrollTop: self.progress * scrollDistance,
          duration: 0.1,
          ease: "none",
        });

        const newActiveIndex = Math.floor(self.progress * (dataFeatures.length - 1e-9));
        setActive(newActiveIndex);
      },
    });

  }, { scope: pinRef });


  return (
    <div className="relative xl:h-[200vh] z-[9999]">
      <motion.h2
        className="font-normal text-2xl md:text-[32px] xl:text-5xl text-[#202020] text-center mb-8 px-4 xl:px-[412px]"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: false, amount: 0.3 }}
      >
        {dataBrandStory.title}
      </motion.h2>
      <div ref={pinRef} className="hidden my-24 h-screen xl:flex flex-col justify-center">

        <div className="relative flex flex-col xl:flex-row xl:justify-center xl:h-[700px] gap-6">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className={`${ibmPlexMono.className} hidden xl:block absolute top-0 left-[130px] text-[#646464] font-normal text-base pl-3 before:content-['•'] before:absolute before:left-0`}
          >
            {t('features')}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: false }}
            className={`${ibmPlexMono.className} hidden xl:block absolute top-0 right-[130px] text-[#646464] font-normal text-base pl-3 before:content-['•'] before:absolute before:left-0`}
          >
            {t('description')}
          </motion.div>

          <div className="xl:w-[412px] flex flex-col text-center space-y-3 xl:space-y-4 xl:absolute xl:top-1/2 xl:left-0 xl:-translate-y-1/2">
            {dataFeatures.map((f, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: false, amount: 0.2 }}
                className={cn(
                  active === i ? "text-[#0066FF]" : "text-[#00000031]",
                  "font-normal text-lg md:text-xl xl:text-[28px] xl:leading-[36px] transition-colors duration-300"
                )}
              >
                {f.feature}
              </motion.span>
            ))}
          </div>

          <div
            ref={scrollContainerRef}
            className="max-w-[448px] w-full h-[500px] xl:h-[700px] overflow-y-hidden space-y-10 xl:space-y-20 mx-auto"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {dataFeatures.map((f, i) => (
              <motion.div
                key={f.feature}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: false, amount: 0.3 }}
                className="relative h-[400px] md:h-[650px] xl:h-full rounded-[40px] md:rounded-[80px] xl:rounded-[120px] overflow-hidden flex justify-center items-center"
              >
                <Image
                  src={f.background}
                  fill
                  alt="background"
                  className="absolute w-full h-full inset-0 object-cover"
                />
                <motion.div
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  viewport={{ once: false }}
                  className="w-[220px] md:w-[280px] xl:w-[321px] h-[260px] md:h-[320px] xl:h-[362px] rounded-[30px] md:rounded-[40px] xl:rounded-[60px] bg-[#FEFEFF] flex justify-center items-center relative z-10"
                >
                  <Image
                    src={f.image}
                    width={260}
                    height={220}
                    alt="graphic feature"
                  />
                </motion.div>
              </motion.div>
            ))}
          </div>

          <div className="xl:max-w-[327px] space-y-3 xl:space-y-2 xl:absolute xl:top-1/2 xl:right-0 xl:-translate-y-1/2">
            {dataFeatures.map((d, i) => {
              const diff = Math.abs(i - active);
              const shrink = diff * 16;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  viewport={{ once: false }}
                >
                  {active === i ? (
                    <div className="p-4 md:p-6 xl:p-8 bg-[#F3F8FF] rounded-2xl max-w-[500px] mx-auto md:rounded-3xl transition-all duration-500">
                      <p className="text-gray-700 text-sm md:text-base">
                        {d.description ?? ""}
                      </p>
                    </div>
                  ) : (
                    <div
                      className="mx-auto hidden xl:block bg-[#F3F8FF] rounded-2xl transition-all duration-500"
                      style={{
                        height: 20,
                        width: `calc(100% - ${shrink}px)`,
                      }}
                    />
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div> 
      <div className="xl:hidden flex flex-col sm:flex-row overflow-x-auto px-4 sm:px-10 space-y-10 sm:space-y-0 gap-x-10 items-stretch" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            {dataFeatures.map((feature, index) => (
              <div key={index} className="space-y-6 sm:min-w-full px-4 pb-4 pt-8 bg-[#FFFFFF] rounded-[40px] shadow-[0px_0px_24px_0px_#00000014]">
                <div className="space-y-4">
                  <h3 className="text-[#0066FF] text-lg font-normal">{feature.feature}</h3>
                  <p className="font-light text-sm text-[#202020]">{feature.description}</p>
                </div>

                <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: false, amount: 0.3 }}
                className="relative min-h-[350px] md:h-[650px] xl:h-full rounded-[32px] md:rounded-[80px] xl:rounded-[120px] overflow-hidden flex justify-center items-center"
              >
                <Image
                  src={feature.background}
                  fill
                  alt="background"
                  className="absolute w-full h-full inset-0 object-cover"
                />
                <motion.div
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  viewport={{ once: false }}
                  className="w-[228px] md:w-[280px] xl:w-[321px] h-[257px] md:h-[320px] xl:h-[362px] rounded-[30px] md:rounded-[40px] xl:rounded-[60px] bg-[#FEFEFF] flex justify-center items-center relative z-10"
                >
                  <Image
                    src={feature.image}
                    width={219}
                    height={206}
                    alt="graphic feature"
                  />
                </motion.div>
              </motion.div>
              </div>
            ))}
      </div>
    </div>
  );
};

export default BrandStorySection;